/**
 * screen-recorder/air-draw.js — optional "draw over your screen recording with
 * hand gestures" orchestrator. Keeps ALL air-draw logic out of the fragile
 * recorder blade/engine; the blade just toggles it and, at record time, hands
 * us the screen stream and records the composite stream we return.
 *
 * Pipeline when enabled:
 *   camera ──► MediaPipe hand-tracking (gesture-draw) ──► neon strokes
 *   screen ──► <video> ──► composite <canvas> (screen frame + strokes baked) ──►
 *             canvas.captureStream() (+ screen audio) ──► ScreenRecorder
 *
 * Activation discipline: the CAMERA is acquired when the user flips the toggle
 * (its own gesture) so that at record time only ONE gesture-gated call remains
 * (getDisplayMedia, owned by the blade). getUserMedia after an awaited
 * getDisplayMedia is unreliable across browsers — this sidesteps it.
 *
 * The strokes are baked into BOTH the live preview and the recording (same
 * composite canvas). The cursor + hand skeleton render to a SEPARATE overlay
 * canvas (renderMode 'aidsOnly') so they guide the user without being recorded.
 *
 * Public API (window.ScreenAirDraw):
 *   init({ selfviewEl, previewEl, onstatus })   — wire DOM (once)
 *   enable() -> Promise<bool>   — acquire camera + start tracking + self-view
 *   disable()                   — release camera + tracking + self-view
 *   isEnabled() / isReady()
 *   setColor(css) / setBrush(px) / clear()
 *   beginComposite(screenStream, { fps }) -> MediaStream   — start compositing,
 *                                 returns the stream to record (+ screen audio)
 *   endComposite()              — stop compositing, restore self-view
 *   teardown()                  — release everything
 */
(function () {
'use strict';
if (window.ScreenAirDraw) return;

var S = {
    inited: false,
    enabled: false,
    ready: false,
    camStream: null,
    selfVideo: null,        // <video> self-view (camera)
    overlay: null,          // single overlay <canvas> for cursor+skeleton (re-parented)
    gd: null,
    selfviewEl: null,       // green-room self-view container
    previewEl: null,        // live composite preview container (during recording)
    onstatus: function () {},
    color: '#34e1eb',
    width: 8,
    // composite (record) state
    comp: null,             // { canvas, ctx, video, raf, stream, screenStream }
};

function el(tag, css) { var e = document.createElement(tag); if (css) e.style.cssText = css; return e; }

window.ScreenAirDraw = {
    init: function (opts) {
        if (S.inited) return;
        opts = opts || {};
        S.selfviewEl = opts.selfviewEl || null;
        S.previewEl = opts.previewEl || null;
        S.onstatus = typeof opts.onstatus === 'function' ? opts.onstatus : function () {};
        S.inited = true;
    },

    isEnabled: function () { return S.enabled; },
    isReady: function () { return S.ready; },
    setColor: function (c) { S.color = c; if (S.gd) S.gd.setColor(c); },
    setBrush: function (px) { S.width = +px || S.width; if (S.gd) S.gd.setBrush(S.width); },
    clear: function () { if (S.gd) S.gd.clear(); },

    enable: function () {
        if (S.enabled) return Promise.resolve(true);
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.GestureDraw) {
            return Promise.resolve(false);
        }
        S.onstatus('loading');
        return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false })
            .then(function (stream) {
                S.camStream = stream;
                // self-view video (mirrored, selfie feel)
                S.selfVideo = el('video', 'width:100%;height:100%;object-fit:cover;transform:scaleX(-1);display:block');
                S.selfVideo.muted = true; S.selfVideo.playsInline = true; S.selfVideo.setAttribute('playsinline', ''); S.selfVideo.autoplay = true;
                S.selfVideo.srcObject = stream;
                // overlay canvas for cursor + skeleton (re-parented between self-view and preview)
                S.overlay = el('canvas', 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none');
                if (S.selfviewEl) {
                    S.selfviewEl.innerHTML = '';
                    S.selfviewEl.appendChild(S.selfVideo);
                    S.selfviewEl.appendChild(S.overlay);
                    S.selfviewEl.hidden = false;
                }
                var p = S.selfVideo.play(); if (p && p.catch) p.catch(function () {});
                // gesture-draw: detect on the camera, mirror like a selfie, paint
                // ONLY the aids (cursor+skeleton) here — strokes are baked via
                // renderInto into the self-view / composite separately.
                S.gd = window.GestureDraw.create({
                    video: S.selfVideo, canvas: S.overlay,
                    mirror: true, renderMode: 'aidsOnly',
                    color: S.color, width: S.width,
                    onready: function () { S.ready = true; S.onstatus('ready'); },
                    onerror: function () { S.onstatus('error'); }
                });
                S.enabled = true;
                // self-view stroke painter: in the green-room we also show the
                // strokes the user test-draws, on a throwaway loop.
                startSelfStrokeLoop();
                var sp = S.gd.start(); if (sp && sp.catch) sp.catch(function () { S.onstatus('error'); });
                return true;
            })
            .catch(function (err) {
                S.onstatus(err && err.name === 'NotAllowedError' ? 'denied' : 'nocam');
                return false;
            });
    },

    disable: function () {
        stopSelfStrokeLoop();
        if (S.comp) this.endComposite();
        if (S.gd) { try { S.gd.destroy(); } catch (e) {} S.gd = null; }
        if (S.camStream) { S.camStream.getTracks().forEach(function (t) { t.stop(); }); S.camStream = null; }
        if (S.selfviewEl) { S.selfviewEl.innerHTML = ''; S.selfviewEl.hidden = true; }
        S.selfVideo = null; S.overlay = null; S.enabled = false; S.ready = false;
    },

    // Build the composite (screen frame + neon strokes) and return the stream to
    // record. screenStream = the getDisplayMedia stream the BLADE acquired.
    beginComposite: function (screenStream, opts) {
        opts = opts || {};
        if (!S.gd || !screenStream) return null;
        var fps = opts.fps && opts.fps > 0 ? opts.fps : 30;
        S.gd.clear(); // green-room scribbles don't carry into the recording

        var sv = el('video');
        sv.muted = true; sv.playsInline = true; sv.setAttribute('playsinline', ''); sv.autoplay = true;
        sv.srcObject = new MediaStream(screenStream.getVideoTracks());
        var pp = sv.play(); if (pp && pp.catch) pp.catch(function () {});

        var vset = {};
        try { vset = screenStream.getVideoTracks()[0].getSettings() || {}; } catch (e) {}
        // «Область экрана» (24.10.2026): доли кадра экрана; холст — её размера.
        var crop = opts.crop && opts.crop.w > 0 && opts.crop.h > 0 ? opts.crop : null;
        var w = even((vset.width || 1280) * (crop ? crop.w : 1)), h = even((vset.height || 720) * (crop ? crop.h : 1));
        if (w < 64) w = 64; if (h < 64) h = 64;
        function drawScreen(src, sw0, sh0) {
            if (!crop) { ctx.drawImage(src, 0, 0, w, h); return; }
            ctx.drawImage(src, crop.x * sw0, crop.y * sh0, crop.w * sw0, crop.h * sh0, 0, 0, w, h);
        }
        var canvas = el('canvas'); canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');

        // live preview: show the composite canvas + move the aids overlay on top
        if (S.previewEl) {
            S.previewEl.innerHTML = '';
            var disp = el('div', 'position:relative;width:100%;height:100%');
            canvas.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;background:#05070d';
            disp.appendChild(canvas);
            if (S.overlay) { S.overlay.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none'; disp.appendChild(S.overlay); }
            S.previewEl.appendChild(disp);
            S.previewEl.hidden = false;
        }
        stopSelfStrokeLoop(); // the composite loop now owns rendering

        // Composite pump — must survive a HIDDEN tab (feedback #1309: the rAF
        // loop stops when the tab is hidden, freezing the recording on its
        // last painted frame; a 26.5-min take came out as one static frame).
        // MSTP-driven: the screen's own frames (which keep flowing in
        // background tabs) drive the draw, and requestFrame() pushes each
        // composite out — no rAF, no compositor. Same design as
        // presenter-overlay.js. Non-MSTP browsers keep the old rAF loop.
        var useMstp = typeof window.MediaStreamTrackProcessor === 'function';
        var stream = canvas.captureStream(useMstp ? 0 : fps);
        // carry the real screen's audio (system audio) into the recorded stream
        screenStream.getAudioTracks().forEach(function (t) { try { stream.addTrack(t); } catch (e) {} });
        var capTrack = stream.getVideoTracks()[0];

        S.comp = { canvas: canvas, ctx: ctx, video: sv, raf: 0, stream: stream, screenStream: screenStream, stopped: false, reader: null };

        if (useMstp) {
            var proc = new MediaStreamTrackProcessor({ track: screenStream.getVideoTracks()[0] });
            var reader = proc.readable.getReader();
            S.comp.reader = reader;
            (async function pump() {
                while (S.comp && !S.comp.stopped) {
                    var r;
                    try { r = await reader.read(); } catch (e) { break; }
                    if (r.done) break;
                    var vf = r.value;
                    try {
                        drawScreen(vf, vf.displayWidth || vf.codedWidth, vf.displayHeight || vf.codedHeight);
                        S.gd.renderInto(ctx, w, h);
                        if (capTrack && capTrack.requestFrame) capTrack.requestFrame();
                    } catch (e) {}
                    try { vf.close(); } catch (e) {}
                }
            })();
        } else {
            (function draw() {
                if (!S.comp || S.comp.stopped) return;
                S.comp.raf = requestAnimationFrame(draw);
                try { if (sv.videoWidth) drawScreen(sv, sv.videoWidth, sv.videoHeight); } catch (e) {}
                try { S.gd.renderInto(ctx, w, h); } catch (e) {}
            })();
        }
        return stream;
    },

    endComposite: function () {
        if (!S.comp) return;
        S.comp.stopped = true;
        if (S.comp.reader) { try { S.comp.reader.cancel().catch(function () {}); } catch (e) {} }
        if (S.comp.raf) cancelAnimationFrame(S.comp.raf);
        try { S.comp.video.pause(); } catch (e) {}
        try { S.comp.video.srcObject = null; } catch (e) {}
        // stop the canvas capture track + the live SCREEN video track (the blade
        // handed us the screen stream; its audio tracks are shared with the
        // recorded stream and get stopped by recorder.stop()).
        try { S.comp.stream.getVideoTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        try { S.comp.screenStream.getVideoTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        S.comp = null;
        if (S.previewEl) { S.previewEl.innerHTML = ''; S.previewEl.hidden = true; }
        // restore the self-view (camera still live if still enabled)
        if (S.enabled && S.selfviewEl && S.selfVideo) {
            S.selfviewEl.innerHTML = '';
            S.selfviewEl.appendChild(S.selfVideo);
            if (S.overlay) { S.overlay.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none'; S.selfviewEl.appendChild(S.overlay); }
            S.selfviewEl.hidden = false;
            startSelfStrokeLoop();
        }
    },

    teardown: function () { this.disable(); },

    // test introspection (drive scripted strokes / inspect the composite)
    _test: { gd: function () { return S.gd; }, comp: function () { return S.comp; } },
};

// In the green-room self-view we paint the test-strokes on a small throwaway
// canvas stacked under the aids overlay, so the user can try drawing before
// recording. (During recording the composite loop does this instead.)
var selfRaf = 0, selfStrokeCanvas = null, selfStrokeCtx = null;
function startSelfStrokeLoop() {
    if (!S.selfviewEl || !S.gd || selfRaf) return;
    selfStrokeCanvas = el('canvas', 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none');
    // insert UNDER the aids overlay
    if (S.overlay && S.overlay.parentNode === S.selfviewEl) S.selfviewEl.insertBefore(selfStrokeCanvas, S.overlay);
    else S.selfviewEl.appendChild(selfStrokeCanvas);
    selfStrokeCtx = selfStrokeCanvas.getContext('2d');
    (function loop() {
        selfRaf = requestAnimationFrame(loop);
        var r = S.selfviewEl.getBoundingClientRect();
        var w = Math.max(2, r.width | 0), h = Math.max(2, r.height | 0);
        if (selfStrokeCanvas.width !== w) selfStrokeCanvas.width = w;
        if (selfStrokeCanvas.height !== h) selfStrokeCanvas.height = h;
        selfStrokeCtx.clearRect(0, 0, w, h);
        try { S.gd.renderInto(selfStrokeCtx, w, h); } catch (e) {}
    })();
}
function stopSelfStrokeLoop() {
    if (selfRaf) { cancelAnimationFrame(selfRaf); selfRaf = 0; }
    if (selfStrokeCanvas && selfStrokeCanvas.parentNode) selfStrokeCanvas.parentNode.removeChild(selfStrokeCanvas);
    selfStrokeCanvas = null; selfStrokeCtx = null;
}

function even(n) { n = Math.round(n || 0); if (n % 2) n += 1; return n < 2 ? 2 : n; }

})();
